import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import WaveSurfer from 'wavesurfer.js';

export interface AudioPlayerConfig {
  waveColor?: string;
  progressColor?: string;
  cursorColor?: string;
  barWidth?: number;
  barGap?: number;
  barRadius?: number;
  height?: number;
  normalize?: boolean;
  hideScrollbar?: boolean;
}

@Component({
  selector: 'app-audio-player',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './audio-player.component.html',
  styleUrl: './audio-player.component.scss',
})
export class AudioPlayerComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('waveform', { static: true }) waveformRef!: ElementRef<HTMLDivElement>;

  // Core inputs
  @Input() src: string = '';
  @Input() autoplay: boolean = false;

  // Appearance
  @Input() waveColor: string = '#4A4F58';
  @Input() progressColor: string = '#FF9E1B';
  @Input() cursorColor: string = '#FFFFFF';
  @Input() barWidth: number = 3;
  @Input() barGap: number = 2;
  @Input() barRadius: number = 2;
  @Input() height: number = 80;

  // Controls visibility
  @Input() showControls: boolean = true;
  @Input() showTime: boolean = true;
  @Input() showDuration: boolean = true;
  @Input() showVolume: boolean = true;
  @Input() compact: boolean = false;

  // Events
  @Output() audioPlay = new EventEmitter<void>();
  @Output() audioPause = new EventEmitter<void>();
  @Output() audioEnd = new EventEmitter<void>();
  @Output() audioReady = new EventEmitter<number>(); // Emits duration
  @Output() timeUpdate = new EventEmitter<number>(); // Emits current time
  @Output() audioError = new EventEmitter<string>();

  // Internal state
  wavesurfer: WaveSurfer | null = null;
  isPlaying: boolean = false;
  isReady: boolean = false;
  isLoading: boolean = false;
  hasAudio: boolean = false;
  hasError: boolean = false;
  currentTime: number = 0;
  duration: number = 0;
  volume: number = 1;
  isMuted: boolean = false;

  ngOnInit() {
    this.initWaveSurfer();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['src'] && this.wavesurfer) {
      const newSrc = changes['src'].currentValue?.trim();
      const oldSrc = changes['src'].previousValue?.trim();

      // Handle src change
      if (newSrc && newSrc !== oldSrc) {
        // New audio URL - reset state and load it
        this.hasError = false;
        this.isPlaying = false;
        this.currentTime = 0;
        this.duration = 0;
        this.loadAudio(newSrc);
      } else if (!newSrc && oldSrc) {
        // Src was cleared - reset state
        this.hasAudio = false;
        this.hasError = false;
        this.isLoading = false;
        this.isReady = false;
        this.isPlaying = false;
        this.currentTime = 0;
        this.duration = 0;
        this.wavesurfer?.empty();
      }
    }
  }

  ngOnDestroy() {
    this.destroyWaveSurfer();
  }

  private initWaveSurfer() {
    if (!this.waveformRef?.nativeElement) return;

    this.wavesurfer = WaveSurfer.create({
      container: this.waveformRef.nativeElement,
      waveColor: this.waveColor,
      progressColor: this.progressColor,
      cursorColor: this.cursorColor,
      barWidth: this.barWidth,
      barGap: this.barGap,
      barRadius: this.barRadius,
      height: this.height,
      normalize: true,
      hideScrollbar: true,
      fillParent: true,
      mediaControls: false,
    });

    this.setupEventListeners();

    if (this.src) {
      this.hasAudio = true;
      this.loadAudio(this.src);
    } else {
      this.hasAudio = false;
      this.isLoading = false;
    }
  }

  private setupEventListeners() {
    if (!this.wavesurfer) return;

    this.wavesurfer.on('ready', () => {
      this.isReady = true;
      this.isLoading = false;
      this.duration = this.wavesurfer?.getDuration() || 0;
      this.audioReady.emit(this.duration);

      if (this.autoplay) {
        this.play();
      }
    });

    this.wavesurfer.on('play', () => {
      this.isPlaying = true;
      this.audioPlay.emit();
    });

    this.wavesurfer.on('pause', () => {
      this.isPlaying = false;
      this.audioPause.emit();
    });

    this.wavesurfer.on('finish', () => {
      this.isPlaying = false;
      this.audioEnd.emit();
    });

    this.wavesurfer.on('timeupdate', (time: number) => {
      this.currentTime = time;
      this.timeUpdate.emit(time);
    });

    this.wavesurfer.on('error', (error: Error) => {
      console.warn('Audio player error:', error.message);
      this.isLoading = false;
      this.isReady = false;
      this.hasError = true;
      this.audioError.emit(error.message);
    });

    this.wavesurfer.on('loading', () => {
      this.isLoading = true;
    });
  }

  private loadAudio(url: string) {
    // Validate URL before loading
    if (!this.wavesurfer || !url || url.trim() === '') {
      this.isLoading = false;
      this.hasAudio = false;
      this.hasError = false;
      return;
    }

    // Check if URL is valid
    try {
      new URL(url);
    } catch {
      console.warn('Invalid audio URL:', url);
      this.isLoading = false;
      this.hasAudio = false;
      this.hasError = true;
      this.audioError.emit('Invalid audio URL');
      return;
    }

    this.hasAudio = true;
    this.hasError = false;
    this.isLoading = true;
    this.isReady = false;

    // Destroy existing wavesurfer and create new one with media element
    // This bypasses CORS issues by using HTML Audio element directly
    this.wavesurfer.destroy();

    // Create Audio element (HTML5 Audio playback doesn't require CORS)
    const audio = new Audio(url);

    // Generate placeholder peaks for waveform visualization
    const peaks = this.generatePlaceholderPeaks();

    // Create new WaveSurfer with the audio element
    this.wavesurfer = WaveSurfer.create({
      container: this.waveformRef.nativeElement,
      waveColor: this.waveColor,
      progressColor: this.progressColor,
      cursorColor: this.cursorColor,
      barWidth: this.barWidth,
      barGap: this.barGap,
      barRadius: this.barRadius,
      height: this.height,
      normalize: true,
      hideScrollbar: true,
      fillParent: true,
      mediaControls: false,
      media: audio,
      peaks: [peaks],
    });

    this.setupEventListeners();
  }

  private generatePlaceholderPeaks(): number[] {
    // Generate random-looking peaks for placeholder waveform
    const numPeaks = 200;
    const peaks: number[] = [];
    for (let i = 0; i < numPeaks; i++) {
      // Create a wave-like pattern
      const baseAmplitude = 0.3 + Math.random() * 0.4;
      const variation = Math.sin(i * 0.3) * 0.2;
      peaks.push(Math.min(1, Math.max(0.1, baseAmplitude + variation)));
    }
    return peaks;
  }

  private destroyWaveSurfer() {
    if (this.wavesurfer) {
      this.wavesurfer.destroy();
      this.wavesurfer = null;
    }
  }

  // Public methods
  play() {
    this.wavesurfer?.play();
  }

  pause() {
    this.wavesurfer?.pause();
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  stop() {
    this.wavesurfer?.stop();
  }

  seek(progress: number) {
    // progress is 0-1
    this.wavesurfer?.seekTo(progress);
  }

  seekTo(time: number) {
    if (this.duration > 0) {
      this.wavesurfer?.seekTo(time / this.duration);
    }
  }

  setVolume(value: number) {
    this.volume = Math.max(0, Math.min(1, value));
    this.wavesurfer?.setVolume(this.volume);
    if (this.volume > 0) {
      this.isMuted = false;
    }
  }

  toggleMute() {
    this.isMuted = !this.isMuted;
    this.wavesurfer?.setVolume(this.isMuted ? 0 : this.volume);
  }

  skipBackward(seconds: number = 10) {
    const newTime = Math.max(0, this.currentTime - seconds);
    this.seekTo(newTime);
  }

  skipForward(seconds: number = 10) {
    const newTime = Math.min(this.duration, this.currentTime + seconds);
    this.seekTo(newTime);
  }

  // Formatting helpers
  formatTime(seconds: number): string {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  get progressPercent(): number {
    if (this.duration === 0) return 0;
    return (this.currentTime / this.duration) * 100;
  }

  onProgressClick(event: MouseEvent) {
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const percent = (event.clientX - rect.left) / rect.width;
    this.seek(percent);
  }

  onVolumeChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setVolume(parseFloat(input.value));
  }
}
